import { SOURCE_PACK_CONTENT_MAX_BYTES } from '@malaberg/shared';
/**
 * MarkdownEditor — T28 reusable component.
 *
 * A simple monospace textarea editor with byte counter and validation.
 * Used by both SourcePackTab and SystemPromptsTab.
 *
 * Props:
 *   value      — current string content
 *   onChange   — called on every keystroke with the new value
 *   label?     — optional label for the textarea (default: "Content")
 *   maxBytes?  — byte limit (default: SOURCE_PACK_CONTENT_MAX_BYTES = 1 048 576)
 *   height?    — CSS height string (default: "400px")
 */
import { Box, Text, Textarea } from '@mantine/core';

export interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  maxBytes?: number;
  height?: string;
}

/** Returns the UTF-8 byte length of a string. */
function byteLength(str: string): number {
  // TextEncoder is available in modern browsers and Node 11+
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str).length;
  }
  // Fallback (Jest/jsdom environments that predate TextEncoder)
  return encodeURIComponent(str).replace(/%[0-9A-Fa-f]{2}/g, '_').length;
}

export function MarkdownEditor({
  value,
  onChange,
  label = 'Content',
  maxBytes = SOURCE_PACK_CONTENT_MAX_BYTES,
  height = '400px',
}: MarkdownEditorProps) {
  const bytes = byteLength(value);
  const isOverLimit = bytes > maxBytes;

  return (
    <Box data-testid="markdown-editor">
      <Textarea
        label={label}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        // Fixed height — scroll internally. Using rows + height (not autosize
        // + maxHeight): TextareaAutosize from react-textarea-autosize errors
        // out when style.maxHeight is set, and tells you to use maxRows
        // instead. The editor needs a hard scroll cap so a 50 KB source-pack
        // file doesn't push the page down, so we go with the fixed-height
        // (non-autosize) variant.
        rows={Math.max(10, Math.floor(Number.parseInt(String(height), 10) / 22))}
        styles={{
          input: {
            fontFamily: 'monospace',
            height,
            maxHeight: height,
            overflowY: 'auto',
            resize: 'vertical',
          },
        }}
        error={
          isOverLimit ? `Content exceeds ${(maxBytes / 1048576).toFixed(0)} MB limit` : undefined
        }
        data-testid="editor-textarea"
      />
      <Text
        size="xs"
        c={isOverLimit ? 'red' : 'dimmed'}
        ta="right"
        mt={4}
        data-testid={isOverLimit ? 'editor-byte-error' : 'editor-byte-counter'}
      >
        {bytes.toLocaleString()} / {maxBytes.toLocaleString()} bytes
      </Text>
    </Box>
  );
}
